import { IconButton, ViewPlaceholder } from '@vrplatform/components';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { Drawer } from '@vrplatform/components-legacy';
import { type Maybe, day, formatCurrency } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Title,
  rem,
} from '@mantine/core';
import { Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ExpenseDescriptionHoverCard,
  ExpenseInfoCard,
  ExpenseLineAmountHoverCard,
} from '../../views/expenses/_components';
import { useListingDetailDrawer } from '../../views/listings/drawer';
import { DrawerCollapsableTable, DrawerHeader } from '../_components';
import { useReservationDetailDrawer } from '../reservation-drawers';
import { ExpenseEllipsisMenuItems } from './ExpenseEllipsisMenuItems';
import { ExpenseEllipsisMenuModals } from './ExpenseEllipsisMenuModals';
import { useExpenseDetailDrawer } from './useExpenseDetailDrawer';
import { isOpeningBalanceTransaction } from '@vrplatform/embed';
import { DepositExpenseTransferIssues } from './DepositExpenseTransferIssues';
import {
  useExpenseQuery,
  type Expense,
} from '../../views/expenses/detail/useExpenseQuery';
import { AppIconById, TagParty } from '../../components';

export const ExpenseDetailDrawer = () => {
  const { opened, close: closeDrawer, expenseId } = useExpenseDetailDrawer();
  const { isLoading, data } = useExpenseQuery(expenseId, 'drawer');
  const goto = useNavigate();

  const expense = data?.expense;

  const [openedModal, setOpenedModal] = useState<
    'delete' | 'edit' | 'payment-status' | 'unreconcile' | null
  >(null);

  const closeModal = () => setOpenedModal(null);

  const openingBalance = isOpeningBalanceTransaction(expense?.uniqueRef);

  return (
    <>
      {expenseId && (
        <ExpenseEllipsisMenuModals
          expense={expense}
          deleteModal={{
            closeModal,
            opened: openedModal === 'delete',
            onSuccess: async () => {
              if (window.location.pathname.startsWith('/expense/'))
                goto('/expenses');
              else closeDrawer();
            },
          }}
          paymentStatusModal={{
            closeModal,
            opened: openedModal === 'payment-status',
          }}
          unreconcileModal={{
            closeModal,
            opened: openedModal === 'unreconcile',
          }}
        />
      )}

      <Drawer opened={opened} onClose={closeDrawer} size={550}>
        <DrawerHeader
          closeDrawer={closeDrawer}
          loading={isLoading}
          type="Expense"
          title={
            expense && (
              <Group wrap="nowrap" gap="xs" mt={rem(5)}>
                <Center w={20}>
                  {expense.connection?.appId ? (
                    <AppIconById appId={expense.connection.appId} />
                  ) : (
                    <Icon icon="UserIcon" size={20} />
                  )}
                </Center>
                <Box>
                  <Title order={4} fw={500} component="p" m={0}>
                    {expense.connection?.name || 'Manual expense'}
                  </Title>
                </Box>
              </Group>
            )
          }
          menuItems={
            expense && (
              <ExpenseEllipsisMenuItems
                expense={expense}
                openDeleteModal={() => setOpenedModal('delete')}
                openPaymentStatusModal={() => setOpenedModal('payment-status')}
                openUnreconcileModal={() => setOpenedModal('unreconcile')}
                isDrawer
              />
            )
          }
        >
          {expense && (
            <IconButton
              onClick={(event) => {
                event.stopPropagation();

                if (openingBalance) {
                  if (openingBalance.type !== 'listing') {
                    goto(`/settings/accounting/opening-balances`);
                  }
                  return;
                }

                goto(`/expense/${expense.id}`);
              }}
              icon="ArrowRightCircleIcon"
              variant="outline"
              tooltip="Go to detail"
            />
          )}
        </DrawerHeader>
        {!expense && !isLoading ? (
          <ViewPlaceholder
            title="No expense found"
            description="Has the expense been deleted?"
            icon="CommentQuestionIcon"
            backgroundColor="transparent"
          />
        ) : (
          <Content expense={expense} isLoading={isLoading} />
        )}
      </Drawer>
    </>
  );
};

const Content = ({
  expense,
  isLoading,
}: {
  expense: Maybe<Expense>;
  isLoading: boolean;
}) => {
  if (!expense) return null;

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
      }}
    >
      <DepositExpenseTransferIssues issues={expense.issues} type="expense" />

      <ExpenseInfoCard type="drawer" expense={expense} />

      <Lines
        lines={expense.lines}
        currency={expense.currency}
        centTotal={expense.centTotal}
      />

      <LoadingOverlay
        visible={isLoading}
        loaderProps={{
          size: 'sm',
        }}
      />
    </Stack>
  );
};

type ExpenseLine = Expense['lines'][number];

const Lines = ({
  lines,
  currency,
  centTotal,
}: {
  lines: ExpenseLine[];
  currency: string;
  centTotal: number;
}) => {
  const { open: openReservation } = useReservationDetailDrawer();
  const { open: openListing } = useListingDetailDrawer();

  const columns = useMemo<MRT_ColumnDef<ExpenseLine>[]>(
    () => [
      {
        header: 'Icon',
        accessorKey: 'id',
        size: 40,
        maxSize: 0,
        minSize: 40,
        grow: false,
        Cell: ({ row }) => {
          const icon = useMemo<IconDefinition>(() => {
            if (row.original.reservation.id) return 'CalendarEventIcon';
            if (row.original.listing.id) return 'HomeIcon';

            return 'HashtagIcon';
          }, [row.original.reservation.id, row.original.listing.id]);

          return <Icon icon={icon} size={20} />;
        },
      },
      {
        header: 'Description',
        accessorKey: 'description',
        Cell: ({ row }) => {
          const reservation = row.original.reservation;
          const listing = row.original.listing;

          if (!reservation.id && !listing.id) {
            return (
              <ExpenseDescriptionHoverCard
                description={row.original.description}
                lineClamp={3}
                dropdownWidth={400}
                dropdownPosition="top"
              />
            );
          }

          if (!reservation.id)
            return (
              <Box>
                <Text component="span" lineClamp={3} size="sm">
                  {row.original.listing.name}
                </Text>
                <ExpenseDescriptionHoverCard
                  description={row.original.description}
                  lineClamp={3}
                  size="xs"
                  color="gray"
                  dropdownWidth={400}
                  dropdownPosition="top"
                />
              </Box>
            );

          return (
            <Box>
              <Text component="span" display="block" size="sm">
                {reservation.guestName} - {reservation.confirmationCode}
              </Text>
              <Text component="span" display="block" size="xs" c="gray">
                {day(reservation.checkIn).format('ll')} -{' '}
                {day(reservation.checkOut).format('ll')}
                {reservation.guests && ` - ${reservation.guests} guests`}
              </Text>
            </Box>
          );
        },
      },
      {
        header: 'Account / Party',
        accessorKey: 'accountTitle',
        Cell: ({ row }) => {
          return (
            <Stack>
              <Text component="span" display="block" size="sm">
                {row.original.accountTitle ?? '-'}
              </Text>
              {row.original.party ? (
                <TagParty party={row.original.party} />
              ) : (
                <Text component="span" display="block" size="xs" c="gray">
                  -
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        header: 'Total',
        accessorKey: 'centTotal',
        mantineTableBodyCellProps: {
          align: 'right',
        },
        size: 100,
        Cell: ({ row }) => {
          return <ExpenseLineAmountHoverCard line={row.original} />;
        },
      },
    ],
    []
  );

  return (
    <DrawerCollapsableTable
      title="Expense lines"
      rightSection={
        <Text fw={500} size="sm">
          {formatCurrency(centTotal / 100, currency)}
        </Text>
      }
      onRowClick={{
        handler: (row) => {
          if (row.original.reservation.id)
            return openReservation(
              row.original.reservation.id,
              'overview',
              'push'
            );

          if (row.original.listing.id)
            return openListing(row.original.listing.id, 'overview', 'push');
        },
        disabled: (row) =>
          !row.original.reservation.id && !row.original.listing.id,
      }}
      rowData={lines}
      columns={columns}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No lines available
          </Text>
        </Center>
      )}
    />
  );
};
